import { useCallback, useEffect, useRef, useState } from "react";
import type { AgentListItem } from "@/types/agent";
import IconCircle from "@/assets/icons/ic-circle-dot-01.svg";
import { TruncatedTooltip } from "@/components/ui/truncated-tooltip";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const MAX_LINES = 2;

interface AgentCardProps {
  agent: AgentListItem;
  onClick: (id: string) => void;
}

export const AgentCard = ({ agent, onClick }: AgentCardProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(agent.categories.length);

  const calculateVisibleItems = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const children = Array.from(container.children) as HTMLElement[];
    if (children.length === 0) return;

    let rowCount = 0;
    let lastTop = -1;
    let count = 0;

    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (child.dataset.more !== undefined) continue;

      if (child.offsetTop > lastTop) {
        rowCount++;
        lastTop = child.offsetTop;
      }

      if (rowCount > MAX_LINES) break;
      count++;
    }

    setVisibleCount(count === 0 ? 1 : count);
  }, []);

  useEffect(() => {
    setVisibleCount(agent.categories.length);
    requestAnimationFrame(calculateVisibleItems);
  }, [agent.categories, calculateVisibleItems]);

  // const visibleCategories = agent.categories.slice(0, visibleCount);
  // const remainingCount = agent.categories.length - visibleCount;

  return (
    <div
      onClick={() => onClick(agent.id)}
      className="flex flex-col gap-4 p-4 lg:p-6 bg-white border border-[rgba(0,0,0,0.15)] rounded-[4px] text-left hover:border-[#202020] transition-colors cursor-pointer"
    >
      <div className="flex items-center gap-3">
        <div className="flex flex-col justify-center gap-1.5 w-full min-w-0">
          <TruncatedTooltip className="text-base font-semibold leading-6 tracking-[-0.01em] text-[#000000] whitespace-normal break-words lg:truncate">
            {agent.name}
          </TruncatedTooltip>

          <div className="flex items-center gap-3">
            <span className="text-xs leading-4 text-[#000000] max-w-[177px] truncate">
              {agent.provider}
            </span>
            <span className="w-px h-[11px] bg-[rgba(0,0,0,0.15)]" />
            <span className="inline-flex items-center gap-1 text-xs leading-4 text-[#000000]">
              <img src={IconCircle} alt="" />
              {agent.selected_count}
            </span>
          </div>
        </div>
      </div>

      <p className="text-sm leading-5 tracking-[-0.01em] text-[#000000] lg:line-clamp-2 self-stretch">
        {agent.short_description}
      </p>
    </div>
  );
};
